import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Crosshair, ListChecks, ShieldAlert } from "lucide-react";

/* Clinic Desk helper — text only, in a panel beside the body.
   No reticle, no pointer on the figure. It rotates through the three steps. */

const CORAL = "#E2604A";
const SCENES = [
  {
    k: "point", Ic: Crosshair, kicker: "01 · POINT AT IT",
    line: "Turn it, and put your finger on the part that hurts.",
  },
  {
    k: "ask", Ic: ListChecks, kicker: "02 · FOUR QUESTIONS",
    line: "How long, how bad, what makes it worse, and what came with it.",
    rows: ["Since this morning", "Worse when I breathe", "No fever"],
  },
  {
    k: "read", Ic: ShieldAlert, kicker: "03 · THE HONEST READ",
    line: "Today, this week, or watch it — and the exact words to use.",
    verdict: { label: "Today, not tomorrow", tone: CORAL },
  },
];

export default function ClinicNarrator() {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const t = setInterval(() => setI((n) => (n + 1) % SCENES.length), 5200);
    return () => clearInterval(t);
  }, []);
  const s = SCENES[i];

  return (
    <div data-testid="clinic-narrator">
      <div className="rounded-[1.4rem] px-5 py-4" style={{ background: "rgba(226,96,74,0.09)", border: "1px solid rgba(226,96,74,0.22)" }}>
        <AnimatePresence mode="wait">
          <motion.div key={s.k} initial={{ opacity: 0, y: 14, filter: "blur(6px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} exit={{ opacity: 0, y: -12, filter: "blur(6px)" }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
            <p className="text-[9.5px] tracking-[0.2em] font-semibold inline-flex items-center gap-2" style={{ color: "#FFA98F" }}>
              <s.Ic size={11} /> {s.kicker}
            </p>
            <p className="font-display text-[17px] sm:text-[19px] text-white leading-snug mt-2">{s.line}</p>

            {s.rows && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {s.rows.map((r, n) => (
                  <motion.span key={r} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 + n * 0.14 }}
                    className="rounded-full px-3 py-1.5 text-[11.5px]" style={{ background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.75)" }}>{r}</motion.span>
                ))}
              </div>
            )}

            {s.verdict && (
              <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.26, type: "spring", damping: 16 }}
                className="inline-flex items-center gap-2.5 rounded-full px-4 py-2 mt-3" style={{ background: s.verdict.tone }}>
                <span className="w-1.5 h-1.5 rounded-full bg-white pulse-soft" />
                <span className="text-[12.5px] font-semibold text-white">{s.verdict.label}</span>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex gap-1.5 mt-4">
          {SCENES.map((x, n) => (
            <button key={x.k} onClick={() => setI(n)} className="h-[3px] rounded-full transition-all" aria-label={x.kicker}
              style={{ width: n === i ? 26 : 10, background: n === i ? CORAL : "rgba(255,255,255,0.22)" }} data-testid={`narrator-dot-${n}`} />
          ))}
        </div>
      </div>
    </div>
  );
}
